/**
 * O que significa "ter perfil profissional", sem nenhuma chamada de API.
 *
 * Separado de `perfilProfissionalService` pela mesma razão de `verificacaoStatus`: aquele módulo
 * importa o `api` (axios), o Jest do CRA não transforma o axios ESM, e componente que só precisa
 * comparar um status não pode arrastar o cliente HTTP para dentro do próprio teste.
 *
 * A regra estava escrita em dois lugares que podiam divergir — a allowlist de `Usuario/Editar` e o
 * `!!data.profissional` do `usePerfilProfissional` — e divergiam: quem desativava o perfil
 * continuava recebendo o Dashboard de profissional, porque a linha em `profissionais` não some,
 * ela vai para INATIVO. Espelha `PerfilProfissionalService.ehStatusProfissional` no backend.
 */

/** Espelha br.com.novafaxina.api.model.ProfissionalStatus. */
export const STATUS_PROFISSIONAL = {
    PENDENTE:  'PENDENTE',
    ATIVO:     'ATIVO',
    INATIVO:   'INATIVO',
    SUSPENSO:  'SUSPENSO',
    REPROVADO: 'REPROVADO',
};

/**
 * Situações em que a pessoa é profissional agora.
 *
 * Allowlist explícita de propósito: um status novo entra como "não profissional" por padrão, em vez
 * de passar despercebido. PENDENTE (plano Livre, ou primeira mensalidade do Pro não paga) e
 * SUSPENSO (inadimplente ou estorno) continuam sendo profissionais — precisam das seções que os deixam regularizar. INATIVO fica de
 * fora: é quem desligou o perfil por escolha própria.
 */
export const STATUS_COM_PERFIL = [
    STATUS_PROFISSIONAL.PENDENTE,
    STATUS_PROFISSIONAL.ATIVO,
    STATUS_PROFISSIONAL.SUSPENSO,
];

/** `profissional` é o objeto de `GET /usuario/{id}`; ausente significa cliente que nunca ativou. */
export function temPerfilProfissional(profissional) {
    return !!profissional && STATUS_COM_PERFIL.includes(profissional.status);
}

/**
 * true para o ex-profissional: existe a linha em `profissionais`, mas ela está desligada.
 *
 * A distinção importa na tela — quem já foi profissional reativa preservando avaliações, histórico
 * e a verificação de identidade já aprovada, e é isso que a tela promete antes do clique. Quem
 * nunca foi começa do zero.
 */
export function podeReativarPerfil(profissional) {
    return !!profissional && profissional.status === STATUS_PROFISSIONAL.INATIVO;
}

/**
 * true para o profissional fora da busca por pausa (férias, folga). Espelha
 * `PerfilProfissionalService.estaPausado` no backend.
 *
 * `!== true`, e não `=== false`: a busca filtra `ativo = true`, então o legado com `ativo` nulo também
 * está fora dela. Dizer "seu perfil aparece nas buscas" a quem não aparece seria pior que mostrar a
 * pausa — e o "Voltar a aparecer" resolve o caso dele também.
 */
export function estaPausado(profissional) {
    return temPerfilProfissional(profissional) && profissional.ativo !== true;
}

/**
 * true só para a pausa feita pela tela (`ativo === false`). É o critério de quem RECEBE pedido —
 * Agendar e `/agenda/add` —, que não deve mudar para o legado com `ativo` nulo, agendável desde sempre.
 */
export function recusaPedidosPorPausa(profissional) {
    return !!profissional && profissional.ativo === false;
}
